import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Eye, ArrowUp, ArrowDown, ClipboardCheck, Check, X, Clock, Users, FileText } from 'lucide-react';
import ECCNominationApprovalModal from './ECCNominationApprovalModal';
import ECCNomineeListPDF from './ECCNomineeListPDF';

const API_BASE_URL = `${process.env.REACT_APP_API_URL.replace(/\/$/, '')}/api`;

const ECCNominationTab = ({ activeSubTab }) => {
  const [showExportModal, setShowExportModal] = useState(false);

  // Search states
  const [pendingSearch, setPendingSearch] = useState('');
  const [approvedSearch, setApprovedSearch] = useState('');
  const [rejectedSearch, setRejectedSearch] = useState('');
  const [expiredSearch, setExpiredSearch] = useState('');
  const [filter, setFilter] = useState('');

  // Dane dla różnych statusów
  const [pendingNominations, setPendingNominations] = useState([]);
  const [approvedNominations, setApprovedNominations] = useState([]);
  const [rejectedNominations, setRejectedNominations] = useState([]);
  const [expiredNominations, setExpiredNominations] = useState([]);

  // Stany ładowania i paginacji
  const [loading, setLoading] = useState({ pending: false, approved: false, rejected: false, expired: false });
  const [currentPage, setCurrentPage] = useState({ pending: 1, approved: 1, rejected: 1, expired: 1, nominees: 1 });
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Sortowanie
  const [sortConfig, setSortConfig] = useState({
    pending: { key: 'nomination', direction: 'asc' },
    approved: { key: 'nomination', direction: 'asc' },
    rejected: { key: 'nomination', direction: 'asc' },
    expired: { key: 'nomination', direction: 'asc' },
    nominees: { key: 'user_name', direction: 'asc' },
  });

  // Określanie widoczności kart z useMemo
  const visibleCards = useMemo(
    () => ({
      pending: activeSubTab === 'nominations-pending',
      approved: activeSubTab === 'nominations-approved',
      rejected: activeSubTab === 'nominations-rejected',
      expired: activeSubTab === 'nominations-expired',
      nominees: activeSubTab === 'nominations-nominees',
    }),
    [activeSubTab]
  );

  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [nominationToApprove, setNominationToApprove] = useState(null);

  // Pobieranie danych
  const fetchNominationsByStatus = async (token, status, setData, loadingKey) => {
    try {
      setLoading((prev) => ({ ...prev, [loadingKey]: true }));
      const response = await axios.get(`${API_BASE_URL}/nomination/`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { status },
      });
      // Zabezpieczenie - upewnij się, że dane są tablicą
      const data = Array.isArray(response.data) ? response.data : [];
      setData(data);
    } catch (error) {
      console.error(`Błąd podczas pobierania nominacji (${status}):`, error.message, error.response?.data);
      setData([]); // Ustaw pustą tablicę w przypadku błędu
    } finally {
      setLoading((prev) => ({ ...prev, [loadingKey]: false }));
    }
  };

  const [allWorkScopes, setAllWorkScopes] = useState([]);
  const [selectedWorkScopes, setSelectedWorkScopes] = useState('');

  const fetchWorkScopes = async (token) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/workscopes/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAllWorkScopes(response.data);
    } catch (error) {
      console.error('Błąd podczas pobierania work scopes:', error.message, error.response?.data);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token) return;

    if (visibleCards.pending) {
      fetchNominationsByStatus(token, 'signed', setPendingNominations, 'pending');
    }
    if (visibleCards.approved || visibleCards.nominees) {
      fetchNominationsByStatus(token, 'approved', setApprovedNominations, 'approved');
    }
    if (visibleCards.rejected) {
      fetchNominationsByStatus(token, 'rejected', setRejectedNominations, 'rejected');
    }
    if (visibleCards.expired) {
      fetchNominationsByStatus(token, 'expired', setExpiredNominations, 'expired');
    }
    fetchWorkScopes(token);
  }, [activeSubTab, visibleCards.pending, visibleCards.approved, visibleCards.rejected, visibleCards.expired, visibleCards.nominees]);


  const formatDate = (date) => (date ? new Date(date).toLocaleDateString('pl-PL') : 'N/A');
  const formatDateTime = (dateTime) =>
    dateTime
      ? new Date(dateTime).toLocaleString('pl-PL', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
        })
      : 'N/A';

  // Dynamiczny tytuł z ikoną w zależności od aktywnej zakładki
  const getPageTitle = () => {
    switch (activeSubTab) {
      case 'nominations-pending':
        return { title: 'Pending Nominations', Icon: FileText };
      case 'nominations-approved':
        return { title: 'Approved Nominations', Icon: Check };
      case 'nominations-rejected':
        return { title: 'Rejected Nominations', Icon: X };
      case 'nominations-expired':
        return { title: 'Expired Nominations', Icon: Clock };
      case 'nominations-nominees':
        return { title: 'Nominee List', Icon: Users };
      default:
        return { title: 'Nominations', Icon: ClipboardCheck };
    }
  };

  const nominationColumnDefinitions = (status) => ({
    nomination: {
      label: 'Nomination',
      render: (item) => <span className="font-semibold text-graphite-900">{item.nomination_number || 'N/A'}</span>,
    },
    requester: {
      label: 'Requester',
      render: (item) => <div className="text-graphite-700 font-medium">{item.user_name || 'N/A'}</div>,
    },
    company: {
      label: 'Company',
      render: (item) => <div className="text-graphite-600 font-medium">{item.company_name || 'N/A'}</div>,
    },
    date: {
      label:
        status === 'signed'
          ? 'Submitted'
          : status === 'approved'
          ? 'Approved'
          : status === 'rejected'
          ? 'Rejected'
          : 'Expired',
      render: (item) => {
        const date =
          status === 'signed'
            ? item.date_signed
            : status === 'approved'
            ? item.date_approved
            : status === 'rejected'
            ? item.date_rejected
            : item.date_expired;
        return <div className="text-graphite-600">{formatDateTime(date)}</div>;
      },
    },
    dateRange: {
      label: 'Valid Until',
      render: (item) => <div className="text-graphite-600">{formatDate(item.date_end)}</div>,
    },
    workScope: {
      label: 'Auth. Level',
      render: (item) => (
        <div className="text-graphite-600">
          {Array.isArray(item.work_scope_data) && item.work_scope_data.length > 0 ? (
            item.work_scope_data.length > 1 ? (
              <div className="text-graphite-500 text-xs">more than one Authorization levels</div>
            ) : (
              item.work_scope_data.slice(0, 2).map((scope, index) => <div key={index}>{scope.name || 'N/A'}</div>)
            )
          ) : (
            <span className="text-graphite-400 text-sm italic">No authorization levels</span>
          )}
        </div>
      ),
    },
  });

  const nominationColumns = ['nomination', 'requester', 'company', 'date', 'dateRange', 'workScope'];

  const filterNominations = (nominations, searchTerm) => {
    if (!searchTerm) return nominations;
    const lowerCaseSearch = searchTerm.toLowerCase();
    return nominations.filter((nomination) =>
      (
        (nomination.user_name || '').toLowerCase().includes(lowerCaseSearch) ||
        (nomination.company_name || '').toLowerCase().includes(lowerCaseSearch) ||
        (nomination.nomination_number || '').toLowerCase().includes(lowerCaseSearch) ||
        ['date_signed', 'date_approved', 'date_rejected', 'date_expired', 'date_start', 'date_end'].some(
          (field) =>
            nomination[field] &&
            (formatDate(nomination[field]).includes(lowerCaseSearch) ||
              (field !== 'date_start' && field !== 'date_end' && formatDateTime(nomination[field]).includes(lowerCaseSearch)))
        )
      )
    );
  };

  const sortNominations = (nominations, view) => {
    const { key, direction } = sortConfig[view];
    const sorted = [...nominations];

    sorted.sort((a, b) => {
      if (key === 'nomination') {
        const aValue = a.nomination_number || '';
        const bValue = b.nomination_number || '';
        if (!aValue && !bValue) return 0;
        if (!aValue) return direction === 'asc' ? 1 : -1;
        if (!bValue) return direction === 'asc' ? -1 : 1;
        return direction === 'asc'
          ? String(aValue).localeCompare(String(bValue))
          : String(bValue).localeCompare(String(aValue));
      } else if (key === 'requester' || key === 'company') {
        const aValue = key === 'requester' ? a.user_name || '' : a.company_name || '';
        const bValue = key === 'requester' ? b.user_name || '' : b.company_name || '';
        if (!aValue && !bValue) return 0;
        if (!aValue) return direction === 'asc' ? 1 : -1;
        if (!bValue) return direction === 'asc' ? -1 : 1;
        return direction === 'asc'
          ? String(aValue).localeCompare(String(bValue))
          : String(bValue).localeCompare(String(aValue));
      } else if (key === 'date') {
        const aDateRaw =
          a.status === 'signed'
            ? a.date_signed
            : a.status === 'approved'
            ? a.date_approved
            : a.status === 'rejected'
            ? a.date_rejected
            : a.date_expired;
        const bDateRaw =
          b.status === 'signed'
            ? b.date_signed
            : b.status === 'approved'
            ? b.date_approved
            : b.status === 'rejected'
            ? b.date_rejected
            : b.date_expired;
        const aDate = aDateRaw ? new Date(aDateRaw) : null;
        const bDate = bDateRaw ? new Date(bDateRaw) : null;
        if (!aDate && !bDate) return 0;
        if (!aDate) return direction === 'asc' ? 1 : -1;
        if (!bDate) return direction === 'asc' ? -1 : 1;
        return direction === 'asc' ? aDate - bDate : bDate - aDate;
      } else if (key === 'dateRange') {
        const aDateEnd = a.date_end ? new Date(a.date_end) : null;
        const bDateEnd = b.date_end ? new Date(b.date_end) : null;
        if (!aDateEnd && !bDateEnd) return 0;
        if (!aDateEnd) return direction === 'asc' ? 1 : -1;
        if (!bDateEnd) return direction === 'asc' ? -1 : 1;
        return direction === 'asc' ? aDateEnd - bDateEnd : bDateEnd - aDateEnd;
      }
      return 0;
    });

    return sorted;
  };

  const sortNominees = (nominees, view) => {
    const { key, direction } = sortConfig[view];
    const sorted = [...nominees];

    sorted.sort((a, b) => {
      if (key === 'user_name' || key === 'company_name') {
        const aValue = a[key] || '';
        const bValue = b[key] || '';
        if (!aValue && !bValue) return 0;
        if (!aValue) return direction === 'asc' ? 1 : -1;
        if (!bValue) return direction === 'asc' ? -1 : 1;
        return direction === 'asc'
          ? String(aValue).localeCompare(String(bValue))
          : String(bValue).localeCompare(String(aValue));
      } else if (key === 'sepEExpiryDate' || key === 'sepDExpiryDate') {
        const aDate = a[key] ? new Date(a[key]) : null;
        const bDate = b[key] ? new Date(b[key]) : null;
        if (!aDate && !bDate) return 0;
        if (!aDate) return direction === 'asc' ? 1 : -1;
        if (!bDate) return direction === 'asc' ? -1 : 1;
        return direction === 'asc' ? aDate - bDate : bDate - aDate;
      }
      return 0;
    });

    return sorted;
  };

  const paginate = (data, page) => {
    const startIndex = (page - 1) * itemsPerPage;
    return data.slice(startIndex, startIndex + itemsPerPage);
  };

  const getTotalPages = (data) => Math.ceil(data.length / itemsPerPage);

  const handlePageChange = (loadingKey, page) => {
    setCurrentPage((prev) => ({ ...prev, [loadingKey]: page }));
  };

  const handleSort = (view, key) => {
    if (key === 'workScope') return; // Bez sortowania dla Auth. Level
    setSortConfig((prev) => {
      const currentDirection = prev[view].key === key && prev[view].direction === 'asc' ? 'desc' : 'asc';
      return { ...prev, [view]: { key, direction: currentDirection } };
    });
    handlePageChange(view, 1); // Reset strony po sortowaniu
  };

  const renderSortIndicator = (view, columnKey) => {
    const { key, direction } = sortConfig[view];
    if (key !== columnKey || columnKey === 'workScope') return null;
    return direction === 'asc' ? (
      <ArrowUp className="h-3 w-3 ml-1" strokeWidth={1.5} />
    ) : (
      <ArrowDown className="h-3 w-3 ml-1" strokeWidth={1.5} />
    );
  };

  const filterData = (data) => {
    // Zabezpieczenie - jeśli data nie jest tablicą, zwróć pustą tablicę
    if (!Array.isArray(data)) {
      console.error('filterData: data is not an array', data);
      return [];
    }
    return data.filter((item) => {
      const matchesText =
        !filter ||
        (item.user_name || '').toLowerCase().includes(filter.toLowerCase()) ||
        (item.company_name || '').toLowerCase().includes(filter.toLowerCase());
      const matchesWorkScopes =
        !selectedWorkScopes ||
        (Array.isArray(item.work_scope_data) &&
          item.work_scope_data.some((scope) => scope.name === selectedWorkScopes));
      return matchesText && matchesWorkScopes;
    });
  };

  const groupedNominees = useMemo(() => {
    const groups = {};
    filterData(approvedNominations).forEach((nom) => {
      const key = nom.user_data?.id;
      if (!key) return;
      if (!groups[key]) {
        groups[key] = { ...nom, work_scope_data: nom.work_scope_data ? [...nom.work_scope_data] : [] };
      } else {
        nom.work_scope_data?.forEach((scope) => {
          if (!groups[key].work_scope_data.some((s) => s.id === scope.id)) {
            groups[key].work_scope_data.push(scope);
          }
        });
      }
      const qualifications = nom.user_data.qualifications || [];
      const sepEQualification = qualifications.find((q) =>
        q.qualification_type_data?.name.toLowerCase().includes('sep e')
      );
      const sepDQualification = qualifications.find((q) =>
        q.qualification_type_data?.name.toLowerCase().includes('sep d')
      );
      groups[key].sepEExpiryDate = sepEQualification ? sepEQualification.expiry_date : null;
      groups[key].sepDExpiryDate = sepDQualification ? sepDQualification.expiry_date : null;
    });
    return Object.values(groups);
  }, [approvedNominations, filter, selectedWorkScopes]);

  // Reset to first page when items per page changes or when filtered results change
  useEffect(() => {
    const activeNominations = visibleCards.pending ? pendingNominations :
                             visibleCards.approved ? approvedNominations :
                             visibleCards.rejected ? rejectedNominations :
                             visibleCards.expired ? expiredNominations :
                             visibleCards.nominees ? groupedNominees :
                             [];
    const calculatedTotalPages = Math.ceil(activeNominations.length / itemsPerPage) || 1;
    const currentKey = visibleCards.pending ? 'pending' :
                      visibleCards.approved ? 'approved' :
                      visibleCards.rejected ? 'rejected' :
                      visibleCards.expired ? 'expired' :
                      'nominees';
    if (currentPage[currentKey] > calculatedTotalPages && calculatedTotalPages > 0) {
      handlePageChange(currentKey, 1);
    }
  }, [itemsPerPage, pendingNominations.length, approvedNominations.length, rejectedNominations.length, expiredNominations.length, groupedNominees.length, activeSubTab, visibleCards, groupedNominees, currentPage]);

  const handleRowDoubleClick = (nomination) => {
    setNominationToApprove(nomination);
    setShowApprovalModal(true);
  };

  const renderTable = (nominations, loadingKey, pageTitle, status) => {
    const { title, Icon } = pageTitle; // Destrukturyzacja obiektu pageTitle
    const filteredNominations = filterNominations(
      nominations,
      loadingKey === 'pending'
        ? pendingSearch
        : loadingKey === 'approved'
        ? approvedSearch
        : loadingKey === 'rejected'
        ? rejectedSearch
        : expiredSearch
    );
    const sortedNominations = sortNominations(filteredNominations, loadingKey);
    const totalPages = getTotalPages(sortedNominations);
    const current = currentPage[loadingKey];
    const paginatedNominations = paginate(sortedNominations, current);
    const isPending = loadingKey === 'pending';
    const columns = nominationColumnDefinitions(status);

    const getVisiblePages = () => {
      const maxVisiblePages = 5; // Bieżąca strona + 2 do przodu + 2 do tyłu
      const pages = [];
      const startPage = Math.max(1, current - 2);
      const endPage = Math.min(totalPages, current + 2);

      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
      return pages;
    };

    return (
      <div className="h-screen flex flex-col">
        <div className="flex-shrink-0 p-4">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
            <h1 className="flex items-center text-2xl font-bold text-graphite-900">
              <Icon className="h-6 w-6 mr-3 text-brand-500" />
              {title}
            </h1>
          </div>
        </div>

        <div className="flex-1 px-4 pb-4 overflow-hidden">
          <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-steel-200 h-full flex flex-col">
            <div className="p-4 flex-shrink-0 border-b-2 border-brand-500 bg-steel-50">
              <div className="flex flex-col md:flex-row items-center gap-4">
                <div className="relative flex-1 w-full">
                  <input
                    placeholder="Search by name, company, date..."
                    className="w-full pl-10 pr-4 py-2.5 border border-steel-300 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-graphite-900 placeholder:text-graphite-400"
                    type="text"
                    value={
                      loadingKey === 'pending'
                        ? pendingSearch
                        : loadingKey === 'approved'
                        ? approvedSearch
                        : loadingKey === 'rejected'
                        ? rejectedSearch
                        : expiredSearch
                    }
                    onChange={(e) => {
                      if (loadingKey === 'pending') setPendingSearch(e.target.value);
                      else if (loadingKey === 'approved') setApprovedSearch(e.target.value);
                      else if (loadingKey === 'rejected') setRejectedSearch(e.target.value);
                      else setExpiredSearch(e.target.value);
                      handlePageChange(loadingKey, 1);
                    }}
                  />
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="lucide lucide-search absolute left-3 top-1/2 transform -translate-y-1/2 text-graphite-400 h-5 w-5"
                  >
                    <circle cx="11" cy="11" r="8" />
                    <path d="m21 21-4.3-4.3" />
                  </svg>
                </div>
              </div>
            </div>
            
            {/* Table container */}
            <div className="flex-1 overflow-hidden flex flex-col">
              <div className="flex-1 overflow-y-auto">
                <table className="min-w-full divide-y divide-steel-200">
                  <thead className="bg-steel-100 sticky top-0 z-10">
                    <tr>
                      {nominationColumns.map((key) => (
                        <th
                          key={key}
                          className="px-4 py-3 text-left text-xs font-semibold text-graphite-700 uppercase tracking-wider"
                        >
                          <button
                            className="flex items-center gap-1 hover:text-brand-500 transition-colors"
                            onClick={() => handleSort(loadingKey, key)}
                          >
                            {columns[key].label}
                            {renderSortIndicator(loadingKey, key)}
                          </button>
                        </th>
                      ))}
                      <th className="px-4 py-3 text-left text-xs font-semibold text-graphite-700 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-steel-100">
                    {loading[loadingKey] ? (
                      <tr>
                        <td colSpan={nominationColumns.length + 1} className="px-4 py-8 text-center text-graphite-500">
                          <div className="flex items-center justify-center gap-2">
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-brand-500"></div>
                            Loading {title.toLowerCase()}...
                          </div>
                        </td>
                      </tr>
                    ) : paginatedNominations.length === 0 ? (
                      <tr>
                        <td colSpan={nominationColumns.length + 1} className="px-4 py-8 text-center text-graphite-500">
                          No {title.toLowerCase()} available.
                        </td>
                      </tr>
                    ) : (
                      paginatedNominations.map((nomination) => (
                        <tr
                          key={nomination.id}
                          className="hover:bg-brand-50/30 transition-colors cursor-pointer"
                          onDoubleClick={() => handleRowDoubleClick(nomination)}
                        >
                          {nominationColumns.map((key) => (
                            <td key={key} className="px-4 py-3 whitespace-nowrap text-sm">
                              {columns[key].render(nomination)}
                            </td>
                          ))}
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-graphite-500">
                            {isPending ? (
                              <button
                                className="px-4 py-1.5 bg-brand-500 text-white text-xs font-medium rounded-full hover:bg-brand-600 transition-colors"
                                onClick={() => handleRowDoubleClick(nomination)}
                              >
                                Verify
                              </button>
                            ) : (
                              <button
                                className="p-1.5 bg-steel-100 text-graphite-700 rounded-full hover:bg-steel-200 transition-colors"
                                aria-label="View"
                                title="View Details"
                                onClick={() => handleRowDoubleClick(nomination)}
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination */}
              <div className="flex items-center justify-between px-4 py-4 border-t border-steel-200 bg-steel-50">
                <div className="flex items-center gap-4">
                  <div className="text-sm text-graphite-600">
                    Showing <span className="font-medium text-graphite-900">{(current - 1) * itemsPerPage + 1}-{Math.min(current * itemsPerPage, sortedNominations.length)}</span> of <span className="font-medium text-graphite-900">{sortedNominations.length}</span> items
                  </div>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      handlePageChange(loadingKey, 1);
                    }}
                    className="text-sm px-4 py-2 border border-steel-300 rounded-xl bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 min-w-[140px]"
                  >
                    <option value={5}>5 per page</option>
                    <option value={10}>10 per page</option>
                    <option value={25}>25 per page</option>
                    <option value={50}>50 per page</option>
                    <option value={100}>100 per page</option>
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  {current > 1 && (
                    <button
                      className="px-4 py-2 border border-steel-300 text-graphite-700 rounded-xl bg-white hover:bg-steel-50 hover:border-brand-300 flex items-center justify-center transition-colors"
                      onClick={() => handlePageChange(loadingKey, current - 1)}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="lucide lucide-chevron-left h-4 w-4 mr-1"
                      >
                        <path d="m15 18-6-6 6-6" />
                      </svg>
                      Previous
                    </button>
                  )}
                  {getVisiblePages().map((page) => (
                    <button
                      key={page}
                      className={`w-10 h-10 border ${
                        page === current
                          ? 'border-brand-500 bg-brand-500 text-white font-medium shadow-sm'
                          : 'border-steel-300 text-graphite-700 bg-white hover:bg-steel-50 hover:border-brand-300'
                      } rounded-xl flex items-center justify-center transition-colors`}
                      onClick={() => handlePageChange(loadingKey, page)}
                    >
                      {page}
                    </button>
                  ))}
                  {current < totalPages && (
                    <button
                      className="px-4 py-2 border border-steel-300 text-graphite-700 rounded-xl bg-white hover:bg-steel-50 hover:border-brand-300 flex items-center justify-center transition-colors"
                      onClick={() => handlePageChange(loadingKey, current + 1)}
                    >
                      Next
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="lucide lucide-chevron-right h-4 w-4 ml-1"
                      >
                        <path d="m9 18 6-6-6-6" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="h-screen flex flex-col">
      {visibleCards.pending && renderTable(pendingNominations, 'pending', getPageTitle(), 'signed')}
      {visibleCards.approved && renderTable(approvedNominations, 'approved', getPageTitle(), 'approved')}
      {visibleCards.rejected && renderTable(rejectedNominations, 'rejected', getPageTitle(), 'rejected')}
      {visibleCards.expired && renderTable(expiredNominations, 'expired', getPageTitle(), 'expired')}

      {visibleCards.nominees && (
        <div className="h-screen flex flex-col">
          <div className="flex-shrink-0 p-4">
            <div className="flex items-center justify-between">
              <h1 className="flex items-center text-2xl font-bold text-graphite-900">
                <Users className="h-6 w-6 mr-3 text-brand-500" />
                {getPageTitle().title}
              </h1>
              <Button
                className="px-4 py-2 border border-steel-300 text-graphite-700 rounded-xl bg-white hover:bg-steel-50 hover:border-brand-300 transition-colors"
                onClick={() => setShowExportModal(true)}
              >
                Export List to PDF
              </Button>
            </div>
          </div>

          <div className="flex-1 px-4 pb-4 overflow-hidden">
            <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-steel-200 h-full flex flex-col">
              <div className="p-4 flex-shrink-0 border-b-2 border-brand-500 bg-steel-50">
                <div className="flex flex-col md:flex-row justify-between gap-4">
                  <div className="relative flex-1">
                    <input
                      placeholder="Filter by name or company"
                      className="w-full pl-10 pr-4 py-2.5 border border-steel-300 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-graphite-900 placeholder:text-graphite-400"
                      type="text"
                      value={filter}
                      onChange={(e) => setFilter(e.target.value)}
                    />
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="lucide lucide-search absolute left-3 top-1/2 transform -translate-y-1/2 text-graphite-400 h-5 w-5"
                    >
                      <circle cx="11" cy="11" r="8" />
                      <path d="m21 21-4.3-4.3" />
                    </svg>
                  </div>
                  <select
                    value={selectedWorkScopes}
                    onChange={(e) => setSelectedWorkScopes(e.target.value)}
                    className="border border-steel-300 rounded-xl px-4 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-graphite-700"
                  >
                    <option value="">All authorization levels</option>
                    {allWorkScopes.map((scope) => (
                      <option key={scope.id} value={scope.name}>
                        {scope.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              {/* Table container */}
              <div className="flex-1 overflow-hidden flex flex-col">
                <div className="flex-1 overflow-y-auto">
                  <table className="min-w-full divide-y divide-steel-200">
                    <thead className="bg-steel-100 sticky top-0 z-10">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-graphite-700 uppercase tracking-wider">
                          <button
                            className="flex items-center gap-1 hover:text-brand-500 transition-colors"
                            onClick={() => handleSort('nominees', 'user_name')}
                          >
                            Name
                            {renderSortIndicator('nominees', 'user_name')}
                          </button>
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-graphite-700 uppercase tracking-wider">
                          <button
                            className="flex items-center gap-1 hover:text-brand-500 transition-colors"
                            onClick={() => handleSort('nominees', 'company_name')}
                          >
                            Company
                            {renderSortIndicator('nominees', 'company_name')}
                          </button>
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-graphite-700 uppercase tracking-wider">
                          Auth. Level
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-graphite-700 uppercase tracking-wider">
                          <button
                            className="flex items-center gap-1 hover:text-brand-500 transition-colors"
                            onClick={() => handleSort('nominees', 'sepEExpiryDate')}
                          >
                            SEP E
                            {renderSortIndicator('nominees', 'sepEExpiryDate')}
                          </button>
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-graphite-700 uppercase tracking-wider">
                          <button
                            className="flex items-center gap-1 hover:text-brand-500 transition-colors"
                            onClick={() => handleSort('nominees', 'sepDExpiryDate')}
                          >
                            SEP D
                            {renderSortIndicator('nominees', 'sepDExpiryDate')}
                          </button>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-steel-100">
                      {loading.approved ? (
                        <tr>
                          <td colSpan={5} className="px-4 py-8 text-center text-graphite-500">
                            <div className="flex items-center justify-center gap-2">
                              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-brand-500"></div>
                              Loading nominees...
                            </div>
                          </td>
                        </tr>
                      ) : paginate(sortNominees(groupedNominees, 'nominees'), currentPage.nominees).length === 0 ? (
                        <tr>
                          <td colSpan={5} className="px-4 py-8 text-center text-graphite-500">
                            No active nominees found.
                          </td>
                        </tr>
                      ) : (
                        paginate(sortNominees(groupedNominees, 'nominees'), currentPage.nominees).map((nomination, index) => (
                          <tr key={nomination.user_data?.id || index} className="hover:bg-brand-50/30 transition-colors">
                            <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-graphite-900">
                              {nomination.user_name || 'N/A'}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-graphite-600">
                              {nomination.company_name || 'N/A'}
                            </td>
                            <td className="px-4 py-3 text-sm text-graphite-600">
                              {Array.isArray(nomination.work_scope_data) && nomination.work_scope_data.length > 0 ? (
                                nomination.work_scope_data.map((scope, idx) => (
                                  <div key={idx}>{scope.name || 'Unknown'}</div>
                                ))
                              ) : (
                                <span className="text-graphite-400 italic">No authorization levels</span>
                              )}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                              {nomination.sepEExpiryDate ? (
                                <span className="text-emerald-600 font-medium">{formatDate(nomination.sepEExpiryDate)}</span>
                              ) : (
                                <span className="text-rose-500">No</span>
                              )}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                              {nomination.sepDExpiryDate ? (
                                <span className="text-emerald-600 font-medium">{formatDate(nomination.sepDExpiryDate)}</span>
                              ) : (
                                <span className="text-rose-500">No</span>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                
                {/* Pagination */}
                <div className="flex items-center justify-between px-4 py-4 border-t border-steel-200 bg-steel-50">
                  <div className="flex items-center gap-4">
                    <div className="text-sm text-graphite-600">
                      Showing <span className="font-medium text-graphite-900">{(currentPage.nominees - 1) * itemsPerPage + 1}-{Math.min(currentPage.nominees * itemsPerPage, groupedNominees.length)}</span> of <span className="font-medium text-graphite-900">{groupedNominees.length}</span> items
                    </div>
                    <select
                      value={itemsPerPage}
                      onChange={(e) => {
                        setItemsPerPage(Number(e.target.value));
                        handlePageChange('nominees', 1);
                      }}
                      className="text-sm px-4 py-2 border border-steel-300 rounded-xl bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 min-w-[140px]"
                    >
                      <option value={5}>5 per page</option>
                      <option value={10}>10 per page</option>
                      <option value={25}>25 per page</option>
                      <option value={50}>50 per page</option>
                      <option value={100}>100 per page</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-2">
                    {currentPage.nominees > 1 && (
                      <button
                        className="px-4 py-2 border border-steel-300 text-graphite-700 rounded-xl bg-white hover:bg-steel-50 hover:border-brand-300 flex items-center justify-center transition-colors"
                        onClick={() => handlePageChange('nominees', currentPage.nominees - 1)}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="lucide lucide-chevron-left h-4 w-4 mr-1"
                        >
                          <path d="m15 18-6-6 6-6" />
                        </svg>
                        Previous
                      </button>
                    )}
                    {(() => {
                      const totalPages = getTotalPages(groupedNominees);
                      const pages = [];
                      const startPage = Math.max(1, currentPage.nominees - 2);
                      const endPage = Math.min(totalPages, currentPage.nominees + 2);

                      for (let i = startPage; i <= endPage; i++) {
                        pages.push(i);
                      }
                      return pages.map((page) => (
                        <button
                          key={page}
                          className={`w-10 h-10 border ${
                            page === currentPage.nominees
                              ? 'border-brand-500 bg-brand-500 text-white font-medium shadow-sm'
                              : 'border-steel-300 text-graphite-700 bg-white hover:bg-steel-50 hover:border-brand-300'
                          } rounded-xl flex items-center justify-center transition-colors`}
                          onClick={() => handlePageChange('nominees', page)}
                        >
                          {page}
                        </button>
                      ));
                    })()}
                    {currentPage.nominees < getTotalPages(groupedNominees) && (
                      <button
                        className="px-4 py-2 border border-steel-300 text-graphite-700 rounded-xl bg-white hover:bg-steel-50 hover:border-brand-300 flex items-center justify-center transition-colors"
                        onClick={() => handlePageChange('nominees', currentPage.nominees + 1)}
                      >
                        Next
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="lucide lucide-chevron-right h-4 w-4 ml-1"
                        >
                          <path d="m9 18 6-6-6-6" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showExportModal && <ECCNomineeListPDF nominees={groupedNominees} onClose={() => setShowExportModal(false)} />}
      {nominationToApprove && (
        <ECCNominationApprovalModal
          isOpen={showApprovalModal}
          onClose={() => setShowApprovalModal(false)}
          nomination={nominationToApprove}
          nominations={[...pendingNominations, ...approvedNominations, ...rejectedNominations, ...expiredNominations]}
          refreshNominations={() => {
            const token = localStorage.getItem('access_token');
            if (!token) return;
            if (visibleCards.pending) fetchNominationsByStatus(token, 'signed', setPendingNominations, 'pending');
            if (visibleCards.approved || visibleCards.nominees)
              fetchNominationsByStatus(token, 'approved', setApprovedNominations, 'approved');
            if (visibleCards.rejected) fetchNominationsByStatus(token, 'rejected', setRejectedNominations, 'rejected');
            if (visibleCards.expired) fetchNominationsByStatus(token, 'expired', setExpiredNominations, 'expired');
          }}
        />
      )}
    </div>
  );
};

export default ECCNominationTab;